import {
  BitkitLink,
  BitkitNativeSelect,
  BitkitSegmentedControl,
  BitkitSelect,
  BitkitTextArea,
  BitkitTextInput,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Separator } from '@chakra-ui/react/separator';
import { useContext, useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useDebounceValue } from 'usehooks-ts';

import BranchSelector from '@/components/BranchSelector/BranchSelector';
import MachineTypeSelector from '@/components/MachineTypeSelector/MachineTypeSelector';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import ProviderReauthRequiredAlert from '@/components/ProviderReauthRequiredAlert/ProviderReauthRequiredAlert';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';
import useStartBuildDialogData from '@/pages/App/hooks/useStartBuildDialogData';
import AppDetailsPageProps from '@/pages/App/types/AppDetailsPageProps';
import useLicensePools from '@/pages/workspace/WorkspaceSettingsIntegrationsPage/components/LicensePools/useLicensePools';
import {
  flattenMachineGroups,
  flattenStackGroups,
  getGroupedMachinesByStack,
  getMachineById,
  getStackById,
  toMachineTypeGroup,
} from '@/services/StackAndMachineService';
import { getProviderReauthRequiredError } from '@/utils/errorResponseParser';

import BuildCommands from '../StartBuildDialog/BuildCommands/BuildCommands';
import CustomVars from '../StartBuildDialog/CustomVars/CustomVars';
import FormBlock from '../StartBuildDialog/FormBlock/FormBlock';
import { BuildConfigurationType } from '../StartBuildDialog/types';

interface BuildConfigurationProps {
  app: AppDetailsPageProps['app'];
  buildTriggerToken: string;
  showCommands: boolean;
}

const BASED_ON_TRIGGER_MAP = 'Based on trigger map';

const DEFAULT_STACK_LABEL = 'Default, based on Workflow settings';

export const getFormDataToSend = (formData: BuildConfigurationType) => {
  const { pipelineableId, ...rest } = formData;
  const formDataToSend: BuildConfigurationType = rest;
  if (pipelineableId !== BASED_ON_TRIGGER_MAP) {
    formDataToSend.pipelineableId = pipelineableId;
  }
  return formDataToSend;
};

const BuildConfiguration = (props: BuildConfigurationProps) => {
  const { app, buildTriggerToken, showCommands } = props;

  const { account } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;
  const { control, setValue, watch } = useFormContext<BuildConfigurationType>();

  const formData = watch();
  const buildConfigType = watch('buildConfigType');

  const { data: licensePoolsData, isLoading: isLicensePoolsLoading } = useLicensePools(account.slug, account.isOwner);

  const handleSourceChange = () => {
    setValue('branch', '');
    setValue('commitHash', '');
    setValue('tag', '');
  };

  const DEBOUNCE_TIME = app.usesRepositoryYml ? 500 : 0;

  const [debouncedBranch, setDebouncedBranch] = useDebounceValue(formData.branch, DEBOUNCE_TIME);
  const [debouncedCommitHash, setDebouncedCommitHash] = useDebounceValue(formData.commitHash, DEBOUNCE_TIME);
  const [debouncedTag, setDebouncedTag] = useDebounceValue(formData.tag, DEBOUNCE_TIME);

  let params;

  if (app.usesRepositoryYml) {
    params = {
      branch: '',
      commit_hash: '',
      tag: '',
    };

    if (buildConfigType === 'basic' || formData.source === 'branch') {
      params.branch = debouncedBranch || '';
    }
    if (buildConfigType === 'advanced') {
      if (formData.source === 'commitHash') {
        params.commit_hash = debouncedCommitHash || '';
      }
      if (formData.source === 'tag') {
        params.tag = debouncedTag || '';
      }
    }
  }

  const startBuildDialogData = useStartBuildDialogData({
    appSlug: app.slug,
    params,
  });

  const {
    hasTriggerMap,
    isLoading: isStartBuildDialogDataLoading,
    error: startBuildDialogDataError,
    pipelines,
    workflows,
    groupedStacks,
  } = startBuildDialogData;

  const reauthError = getProviderReauthRequiredError(startBuildDialogDataError);

  const groupedMachines = useMemo(() => {
    if (!startBuildDialogData?.groupedMachines) {
      return [];
    }

    return startBuildDialogData.groupedMachines.map(toMachineTypeGroup);
  }, [startBuildDialogData]);

  const selectedStack = useMemo(
    () => getStackById(flattenStackGroups(groupedStacks), formData.stack),
    [formData.stack, groupedStacks],
  );
  const filteredGroupedMachines = useMemo(
    () => getGroupedMachinesByStack(groupedMachines, selectedStack),
    [groupedMachines, selectedStack],
  );
  const selectedMachineType = useMemo(
    () => getMachineById(flattenMachineGroups(filteredGroupedMachines), formData.machineTypeId),
    [formData.machineTypeId, filteredGroupedMachines],
  );

  useEffect(() => {
    let defaultPipelineOrWorkflow = '';
    if (hasTriggerMap) {
      defaultPipelineOrWorkflow = BASED_ON_TRIGGER_MAP;
    } else {
      if (!workflows?.length && pipelines?.length === 1) {
        defaultPipelineOrWorkflow = `pipeline#${pipelines[0]}`;
      }
      if (!pipelines?.length && workflows?.length === 1) {
        defaultPipelineOrWorkflow = `workflow#${workflows[0]}`;
      }
    }
    if (!formData.pipelineableId) {
      setValue('pipelineableId', defaultPipelineOrWorkflow);
    }
  }, [formData.pipelineableId, hasTriggerMap, pipelines, setValue, workflows]);

  return (
    <>
      {reauthError && (
        <ProviderReauthRequiredAlert
          workspaceSlug={account.slug}
          providerType={reauthError.providerType}
          host={reauthError.host}
          marginBlockEnd="16"
        />
      )}
      <BitkitSegmentedControl
        marginBlockEnd="16"
        onValueChange={({ value }) => {
          setValue('buildConfigType', value as 'basic' | 'advanced');
        }}
        value={buildConfigType}
        size="lg"
        fullWidth
      >
        <BitkitSegmentedControl.Item value="basic">Basic</BitkitSegmentedControl.Item>
        <BitkitSegmentedControl.Item value="advanced">Advanced</BitkitSegmentedControl.Item>
      </BitkitSegmentedControl>
      <FormBlock isLastItem={buildConfigType === 'basic'}>
        {buildConfigType === 'basic' && (
          <Controller
            control={control}
            name="branch"
            render={({ field }) => (
              <BranchSelector
                isQueryEnabled
                label="Source branch"
                isRequired
                selectedBranch={field.value}
                onSelect={(selectedBranchName: string) => {
                  field.onChange(selectedBranchName);
                  setDebouncedBranch(selectedBranchName);
                }}
                slug={app.slug}
              />
            )}
          />
        )}
        {buildConfigType === 'advanced' && (
          <Box display="flex" gap="8" alignItems="flex-end">
            <Controller
              control={control}
              name="source"
              render={({ field }) => (
                <BitkitNativeSelect
                  label="Source"
                  width="212px"
                  onChange={(e) => {
                    field.onChange(e);
                    handleSourceChange();
                  }}
                  value={field.value}
                >
                  <option value="branch">Branch</option>
                  <option value="tag">Tag</option>
                  <option value="commitHash">Commit hash</option>
                </BitkitNativeSelect>
              )}
            />
            {formData.source === 'branch' && (
              <Controller
                control={control}
                name="branch"
                render={({ field }) => (
                  <BranchSelector
                    isQueryEnabled
                    label=""
                    selectedBranch={field.value}
                    onSelect={field.onChange}
                    slug={app.slug}
                  />
                )}
              />
            )}
            {formData.source === 'tag' && (
              <Controller
                control={control}
                name="tag"
                render={({ field: { onChange, ...fieldProps } }) => (
                  <BitkitTextInput
                    label="Tag"
                    labelHidden
                    optional
                    placeholder="e.g. 1.0.0"
                    width="100%"
                    inputProps={{
                      ...fieldProps,
                      onChange: (event) => {
                        onChange(event);
                        setDebouncedTag(event.target.value);
                      },
                    }}
                  />
                )}
              />
            )}
            {formData.source === 'commitHash' && (
              <Controller
                control={control}
                name="commitHash"
                render={({ field: { onChange, ...fieldProps } }) => (
                  <BitkitTextInput
                    label="Commit hash"
                    labelHidden
                    optional
                    placeholder="e.g. 3ce00ffa28e7b357fcaf67bcfa74e14566ed92cf"
                    width="100%"
                    inputProps={{
                      ...fieldProps,
                      onChange: (event) => {
                        onChange(event);
                        setDebouncedCommitHash(event.target.value);
                      },
                    }}
                  />
                )}
              />
            )}
          </Box>
        )}
        <Controller
          control={control}
          name="pipelineableId"
          render={({ field }) => (
            <BitkitNativeSelect
              label="Target"
              placeholder="Select Workflow or Pipeline"
              state={
                isStartBuildDialogDataLoading || (workflows.length === 0 && pipelines.length === 0)
                  ? 'disabled'
                  : undefined
              }
              errorText={
                !isStartBuildDialogDataLoading && workflows.length === 0 && pipelines.length === 0
                  ? 'There are no targets on the selected source'
                  : ''
              }
              {...field}
            >
              {hasTriggerMap && <option>{BASED_ON_TRIGGER_MAP}</option>}
              {pipelines && pipelines.length > 0 && (
                <optgroup label="Pipelines">
                  {pipelines.map((p) => (
                    <option key={p} value={`pipeline#${p}`}>
                      {p}
                    </option>
                  ))}
                </optgroup>
              )}
              {workflows && workflows.length > 0 && (
                <optgroup label="Workflows">
                  {workflows.map((w) => (
                    <option key={w} value={`workflow#${w}`}>
                      {w}
                    </option>
                  ))}
                </optgroup>
              )}
            </BitkitNativeSelect>
          )}
        />
        <Separator />
        <Controller
          control={control}
          name="commitMessage"
          render={({ field: { ref: fieldRef, ...fieldRest } }) => (
            <BitkitTextArea
              ref={fieldRef}
              optional
              label="Message"
              placeholder="e.g. Triggered by Bitrise build trigger URL."
              textareaProps={fieldRest}
            />
          )}
        />
        {buildConfigType === 'advanced' && groupedStacks && (
          <Controller
            control={control}
            name="stack"
            render={({ field: { value, onChange } }) => (
              <BitkitSelect
                label="Stack"
                optional
                placeholder={DEFAULT_STACK_LABEL}
                value={value || ''}
                onValueChange={onChange}
                items={[
                  { label: DEFAULT_STACK_LABEL, value: '' },
                  ...groupedStacks.flatMap((group) =>
                    group.stacks.map((stack) => ({ group: group.label, label: stack.title, value: stack.id })),
                  ),
                ]}
              />
            )}
          />
        )}
        {buildConfigType === 'advanced' && filteredGroupedMachines && (
          <>
            <Controller
              control={control}
              name="machineTypeId"
              render={({ field }) => (
                <MachineTypeSelector
                  groupedMachines={filteredGroupedMachines}
                  label="Machine type"
                  regionId={startBuildDialogData.region}
                  selectedMachineType={selectedMachineType}
                  withDefaultOption
                  value={field.value}
                  onValueChange={field.onChange}
                />
              )}
            />
            {account.isOwner && !isLicensePoolsLoading && licensePoolsData && licensePoolsData.length > 0 && (
              <Controller
                control={control}
                name="licensePoolId"
                render={({ field }) => (
                  <BitkitNativeSelect
                    helperText="You can run your builds on Bitrise machines relying on license keys."
                    label="License pool"
                    optional
                    placeholder="Select License pool"
                    {...field}
                  >
                    {licensePoolsData.map(({ name, id }) => (
                      <option key={name} value={id}>
                        {name}
                      </option>
                    ))}
                  </BitkitNativeSelect>
                )}
              />
            )}
          </>
        )}
        {buildConfigType === 'advanced' && (
          <Controller
            control={control}
            name="priority"
            render={({ field }) => (
              <BitkitTextInput
                label="Priority"
                optional
                helperText={
                  <>
                    Set priority between -100 and +100. Default value is 0. Available on certain plans only.{' '}
                    <BitkitLink
                      colorVariant="purple"
                      href="https://docs.bitrise.io/en/bitrise-ci/configure-builds/configuring-build-settings/build-priority.html"
                      isExternal
                    >
                      Learn more
                    </BitkitLink>
                  </>
                }
                inputProps={{ ...field, type: 'number' }}
              />
            )}
          />
        )}
      </FormBlock>
      {buildConfigType === 'advanced' && (
        <>
          <CustomVars isLastItem={!showCommands} />
          {showCommands && (
            <BuildCommands buildTriggerToken={buildTriggerToken} data={getFormDataToSend(formData)} slug={app.slug} />
          )}
        </>
      )}
    </>
  );
};

export default BuildConfiguration;
